import React, { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/api';
import { formatStockWigOrderNo, isStockWigOrder } from '../../constants/rachelsRoomConfig';
import ProductionSchedule from '../WorkOrderProfile/ProductionSchedule';
import { useLookoutList } from '../../utils/lookoutList';
import { filterNextRooms } from '../../utils/roomUtils';
import { enrichOrderForLookout } from '../../utils/lookoutEnrichment';
import { getCurrentUser } from '../../utils/auth';
import {
  fetchLookoutInbox,
  fetchAddedLookoutShares,
  fetchSentLookoutShares,
  addLookoutShare,
  dismissLookoutShare,
  removeLookoutShare
} from '../../services/lookoutShareService';
import SharePickerModal from './SharePickerModal';
import './PastDueTasks.css';

// SQL Server dates are stored as local time (EST) but mssql serializes them as UTC;
// strip the 'Z' suffix so this parses as local time instead of shifting. Mirrors
// the same helper in DateRangeReport.jsx (the Production Report).
const formatAppointmentDateTime = (dateString) => {
  if (!dateString) return '-';
  const localDateString = String(dateString).replace('Z', '');
  return new Date(localDateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const PastDueTasks = () => {
  const { items: lookoutItems, remove: removeFromLookout, clear: clearLookout } = useLookoutList();
  const [isLookoutOpen, setIsLookoutOpen] = useState(true);
  const [expandedLookoutRows, setExpandedLookoutRows] = useState(new Set());

  const handleToggleLookoutRow = (orderNo) => {
    setExpandedLookoutRows(prev => {
      const next = new Set(prev);
      if (next.has(orderNo)) {
        next.delete(orderNo);
      } else {
        next.add(orderNo);
      }
      return next;
    });
  };

  // ── Lookout List sharing ──────────────────────────────────────────────
  // Shared items are never copied into localStorage - only the order number
  // (+ a customerName/dueDate snapshot for the appointment guess) lives on
  // the server (tbl_LookoutShares). Everything else (barcode, room, next
  // rooms, appointment, stylist) is re-resolved live via enrichOrderForLookout
  // on every fetch, which is what keeps a shared item "linked" instead of a
  // one-time copy.
  const [sharedInbox, setSharedInbox] = useState([]); // not yet added to "My List"
  const [addedShares, setAddedShares] = useState([]); // already added, merges into "My List"
  const [sentShares, setSentShares] = useState([]); // shares this user has sent out
  const [shareEnrichment, setShareEnrichment] = useState({}); // keyed by OrderNo
  const [loadingShares, setLoadingShares] = useState(false);
  const [isSharedInboxOpen, setIsSharedInboxOpen] = useState(true);
  const [sharePickerItems, setSharePickerItems] = useState(null); // null = picker closed
  const [pendingShareIds, setPendingShareIds] = useState(new Set());
  const [openSentPopoverOrderNo, setOpenSentPopoverOrderNo] = useState(null); // which row's "sent to" popover is open
  const sentPopoverRef = useRef(null);

  // Close the "sent to" popover on an outside click - same pattern as the
  // Office/Stylist filter dropdowns in DateRangeReport.jsx.
  useEffect(() => {
    if (openSentPopoverOrderNo === null) return;
    const handleClickOutside = (event) => {
      if (sentPopoverRef.current && !sentPopoverRef.current.contains(event.target)) {
        setOpenSentPopoverOrderNo(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openSentPopoverOrderNo]);

  const refreshShares = useCallback(async () => {
    const currentUser = getCurrentUser();
    if (!currentUser || currentUser === 'System') return;

    setLoadingShares(true);
    try {
      const [inbox, added, sent] = await Promise.all([
        fetchLookoutInbox(currentUser),
        fetchAddedLookoutShares(currentUser),
        fetchSentLookoutShares(currentUser)
      ]);
      setSharedInbox(inbox);
      setAddedShares(added);
      setSentShares(sent);

      const allShares = [...inbox, ...added];
      const enrichResults = await Promise.all(
        allShares.map(share =>
          enrichOrderForLookout(share.OrderNo, {
            customerName: share.CustomerName,
            dueDate: share.DueDate
          }).then(data => [share.OrderNo, data])
        )
      );
      setShareEnrichment(Object.fromEntries(enrichResults));
    } catch (error) {
      console.error('Error fetching shared lookout items:', error);
    } finally {
      setLoadingShares(false);
    }
  }, []);

  useEffect(() => { refreshShares(); }, [refreshShares]);

  const withPendingShare = async (shareId, action) => {
    setPendingShareIds(prev => new Set(prev).add(shareId));
    try {
      await action();
      await refreshShares();
    } catch (error) {
      console.error('Error updating shared lookout item:', error);
      toast.error('Failed to update shared item');
    } finally {
      setPendingShareIds(prev => {
        const next = new Set(prev);
        next.delete(shareId);
        return next;
      });
    }
  };

  const handleAddShare = (share) => withPendingShare(share.ID, () => addLookoutShare(share.ID));
  // Recipient-side: declining from the inbox, or removing an already-added
  // item from "My List". Flags it (IsDismissed) rather than deleting, so the
  // sender still sees it in their "sent" history.
  const handleDismissShare = (share) => withPendingShare(share.ID, () => dismissLookoutShare(share.ID));
  // Sender-side: actually revoking a share they sent, from the "sent" popover.
  const handleRevokeShare = (share) => withPendingShare(share.ID, () => removeLookoutShare(share.ID));

  const sentShareStatus = (share) => {
    if (share.IsDismissed) return { icon: 'fa-times-circle', tone: 'dismissed', title: 'Dismissed - not added' };
    if (share.IsAdded) return { icon: 'fa-check-circle', tone: 'added', title: 'Added to their list' };
    return { icon: 'fa-clock', tone: 'pending', title: 'Not added yet' };
  };

  // "My List" = personal (localStorage) items + shares this user has already
  // added. Shared rows are tagged with source/shareId/sharedBy so the table
  // can route their remove button to the API instead of localStorage, and
  // show who sent them.
  const mergedMyListItems = [
    ...lookoutItems.map(item => ({ ...item, source: 'personal' })),
    ...addedShares.map(share => ({
      orderNo: share.OrderNo,
      customerName: share.CustomerName,
      dueDate: share.DueDate,
      ...(shareEnrichment[share.OrderNo] || {}),
      source: 'shared',
      shareId: share.ID,
      sharedBy: share.SharedBy
    }))
  ];

  // Groups outgoing shares by OrderNo so each row can show a "who did I send
  // this to" indicator without a separate lookup per render.
  const sentSharesByOrder = sentShares.reduce((acc, share) => {
    (acc[share.OrderNo] = acc[share.OrderNo] || []).push(share);
    return acc;
  }, {});

  const openShareWholeList = () => {
    if (mergedMyListItems.length === 0) return;
    setSharePickerItems(mergedMyListItems.map(item => ({
      orderNo: item.orderNo,
      customerName: item.customerName,
      dueDate: item.dueDate
    })));
  };

  const openShareSingle = (item) => {
    setSharePickerItems([{
      orderNo: item.orderNo,
      customerName: item.customerName,
      dueDate: item.dueDate
    }]);
  };

  const [pastDueTasks, setPastDueTasks] = useState([]);
  const [loadingPastDueTasks, setLoadingPastDueTasks] = useState(false);
  const [pastDueRoomFilter, setPastDueRoomFilter] = useState('all'); // 'all' | '1002' | '3001'
  const [pastDueOrderTypeFilter, setPastDueOrderTypeFilter] = useState('all'); // 'all' | 'stock' | 'client'
  const [pastDueOpened, setPastDueOpened] = useState(false); // stays closed (no fetch) until a tab is picked
  const [expandedPastDueRows, setExpandedPastDueRows] = useState(new Set());
  const [daysOverdueSortOrder, setDaysOverdueSortOrder] = useState('desc'); // 'desc' | 'asc'

  const fetchPastDueTasks = async () => {
    try {
      setLoadingPastDueTasks(true);
      const response = await fetch(`${API_URL}/api/rachels-room/past-due-tasks`);
      if (!response.ok) throw new Error('Failed to fetch past due tasks');
      const data = await response.json();
      setPastDueTasks(data);
    } catch (error) {
      console.error('Error fetching past due tasks:', error);
      toast.error('Failed to load past due tasks');
    } finally {
      setLoadingPastDueTasks(false);
    }
  };

  const handleViewPastDueTask = (task) => {
    window.open(`/work-order-profile/${task.OrderNo}`, '_blank');
  };

  const handleTogglePastDueRow = (orderNo) => {
    setExpandedPastDueRows(prev => {
      const next = new Set(prev);
      if (next.has(orderNo)) {
        next.delete(orderNo);
      } else {
        next.add(orderNo);
      }
      return next;
    });
  };

  const openPastDueTasks = () => {
    if (!pastDueOpened) {
      setPastDueOpened(true);
      fetchPastDueTasks();
    }
  };

  const handlePastDueRoomTab = (value) => {
    setPastDueRoomFilter(value);
    openPastDueTasks();
  };

  const handlePastDueOrderTypeTab = (value) => {
    setPastDueOrderTypeFilter(value);
    openPastDueTasks();
  };

  const filteredPastDueTasks = pastDueTasks
    .filter(task => {
      if (pastDueRoomFilter !== 'all' && String(task.RoomNo) !== pastDueRoomFilter) return false;
      if (pastDueOrderTypeFilter === 'stock' && !isStockWigOrder(task.OrderNo)) return false;
      if (pastDueOrderTypeFilter === 'client' && isStockWigOrder(task.OrderNo)) return false;
      return true;
    })
    .sort((a, b) => (
      daysOverdueSortOrder === 'asc'
        ? a.DaysOverdue - b.DaysOverdue
        : b.DaysOverdue - a.DaysOverdue
    ));

  const handleToggleDaysOverdueSort = () => {
    setDaysOverdueSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const renderOrderTypeBadge = (orderNo) => {
    const isStock = isStockWigOrder(orderNo);
    return (
      <span style={{
        display: 'inline-block',
        padding: '3px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        color: 'white',
        whiteSpace: 'nowrap',
        backgroundColor: isStock ? '#27ae60' : '#f00897'
      }}>
        {isStock ? 'Stock' : 'Client'}
      </span>
    );
  };

  return (
    <div className="past-due-tasks-page">
      <div className="lookout-list-panel">
        <div
          onClick={() => setIsLookoutOpen(prev => !prev)}
          className="lookout-list-header"
        >
          <i className={`fas fa-chevron-${isLookoutOpen ? 'down' : 'right'}`} style={{ color: '#d4af37', fontSize: '12px' }}></i>
          <i className="fas fa-bookmark" style={{ color: '#d4af37' }}></i>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>My List</span>
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            {mergedMyListItems.length === 0 ? '(empty)' : `(${mergedMyListItems.length})`}
          </span>
          {mergedMyListItems.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openShareWholeList();
              }}
              className="btn-small lookout-clear-btn"
              title="Share this entire list with a coworker"
            >
              <i className="fas fa-share-alt"></i> Share List
            </button>
          )}
          {lookoutItems.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm('Clear the entire lookout list? This only removes items you personally bookmarked, not items shared with you.')) clearLookout();
              }}
              className="btn-small lookout-clear-btn"
            >
              Clear All
            </button>
          )}
        </div>

        {isLookoutOpen && (
          mergedMyListItems.length === 0 ? (
            <p className="lookout-empty-hint">
              Bookmark orders from the Production Report (Order DB) to build your lookout list. It stays here until you remove items.
            </p>
          ) : (
            <div className="room-tasks-table-container-past-due" style={{ marginTop: '10px' }}>
              <table className="room-tasks-table-container-past-due">
                <thead>
                  <tr>
                    <th>Order No</th>
                    <th>Type</th>
                    <th>Barcode</th>
                    <th>Customer</th>
                    <th>Stylist</th>
                    <th>Due Date</th>
                    <th>Appointment Date</th>
                    <th>Current Room</th>
                    <th>Next Room(s)</th>
                    <th></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {mergedMyListItems.map(item => {
                    const isExpanded = expandedLookoutRows.has(item.orderNo);
                    const isShared = item.source === 'shared';
                    return (
                      <React.Fragment key={`${item.source}-${item.orderNo}`}>
                        <tr
                          className={`past-due-row ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => handleToggleLookoutRow(item.orderNo)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <span className={`expand-icon ${isExpanded ? 'rotated' : ''}`}>▶</span>
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`/work-order-profile/${item.orderNo}`, '_blank');
                              }}
                              className="past-due-order-link"
                            >
                              {formatStockWigOrderNo(item.orderNo)}
                            </span>
                            {isShared && (
                              <span className="lookout-shared-badge" title={`Shared by ${item.sharedBy}`}>
                                via {item.sharedBy}
                              </span>
                            )}
                            {sentSharesByOrder[item.orderNo]?.length > 0 && (
                              <span className="lookout-sent-badge-wrapper">
                                <button
                                  className="lookout-sent-badge"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenSentPopoverOrderNo(prev => prev === item.orderNo ? null : item.orderNo);
                                  }}
                                >
                                  <i className="fas fa-paper-plane"></i> {sentSharesByOrder[item.orderNo].length}
                                </button>
                                {openSentPopoverOrderNo === item.orderNo && (
                                  <div
                                    className="lookout-sent-popover"
                                    ref={sentPopoverRef}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="lookout-sent-popover-title">Shared with</div>
                                    {sentSharesByOrder[item.orderNo].map(share => {
                                      const status = sentShareStatus(share);
                                      return (
                                        <div key={share.ID} className="lookout-sent-popover-row">
                                          <i
                                            className={`fas ${status.icon} lookout-sent-status ${status.tone}`}
                                            title={status.title}
                                          ></i>
                                          <span className="lookout-sent-popover-name">{share.SharedWith}</span>
                                          <button
                                            className="lookout-sent-popover-revoke"
                                            onClick={() => handleRevokeShare(share)}
                                            title="Revoke this share"
                                          >
                                            <i className="fas fa-times"></i>
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </span>
                            )}
                          </td>
                          <td>{renderOrderTypeBadge(item.orderNo)}</td>
                          <td>{item.barcode || '-'}</td>
                          <td>{item.customerName || '-'}</td>
                          <td>{item.meetingWith || '-'}</td>
                          <td>{item.dueDate ? new Date(item.dueDate).toLocaleDateString() : '-'}</td>
                          <td>
                            {item.appointmentMatchType === 'closest-guess' ? (
                              <span
                                title="Best guess: nearest appointment for this customer by due date. Not confirmed via an order link."
                                className="lookout-guess-date"
                              >
                                <i className="fas fa-question-circle"></i>
                                {formatAppointmentDateTime(item.appointmentDate)}
                              </span>
                            ) : (
                              formatAppointmentDateTime(item.appointmentDate)
                            )}
                          </td>
                          <td>{item.currentRoom || '-'}</td>
                          <td>{filterNextRooms(item.nextRooms) || '-'}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => openShareSingle(item)}
                              title="Share this order with a coworker"
                              className="lookout-share-btn"
                            >
                              <i className="fas fa-share-alt"></i>
                            </button>
                          </td>
                          <td>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isShared) {
                                  handleDismissShare({ ID: item.shareId });
                                } else {
                                  removeFromLookout(item.orderNo);
                                }
                              }}
                              disabled={isShared && pendingShareIds.has(item.shareId)}
                              title="Remove from My List"
                              className="lookout-remove-btn"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="expanded-content">
                            <td colSpan={11}>
                              <ProductionSchedule orderNo={item.orderNo} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      <div className="lookout-list-panel">
        <div
          onClick={() => setIsSharedInboxOpen(prev => !prev)}
          className="lookout-list-header"
        >
          <i className={`fas fa-chevron-${isSharedInboxOpen ? 'down' : 'right'}`} style={{ color: '#d4af37', fontSize: '12px' }}></i>
          <i className="fas fa-inbox" style={{ color: '#d4af37' }}></i>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Shared with You</span>
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            {loadingShares ? 'checking...' : sharedInbox.length === 0 ? '(none)' : `(${sharedInbox.length})`}
          </span>
        </div>

        {isSharedInboxOpen && (
          sharedInbox.length === 0 ? (
            <p className="lookout-empty-hint">
              Nothing new has been shared with you. Orders a coworker shares with you show up here until you add or dismiss them.
            </p>
          ) : (
            <div className="room-tasks-table-container-past-due" style={{ marginTop: '10px' }}>
              <table className="room-tasks-table-container-past-due">
                <thead>
                  <tr>
                    <th>Order No</th>
                    <th>Type</th>
                    <th>Barcode</th>
                    <th>Customer</th>
                    <th>Stylist</th>
                    <th>Due Date</th>
                    <th>Appointment Date</th>
                    <th>Current Room</th>
                    <th>Next Room(s)</th>
                    <th>Shared By</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sharedInbox.map(share => {
                    const enrichment = shareEnrichment[share.OrderNo] || {};
                    const isPending = pendingShareIds.has(share.ID);
                    return (
                      <tr key={share.ID}>
                        <td>
                          <span
                            onClick={() => window.open(`/work-order-profile/${share.OrderNo}`, '_blank')}
                            className="past-due-order-link"
                          >
                            {formatStockWigOrderNo(share.OrderNo)}
                          </span>
                        </td>
                        <td>{renderOrderTypeBadge(share.OrderNo)}</td>
                        <td>{enrichment.barcode || '-'}</td>
                        <td>{share.CustomerName || '-'}</td>
                        <td>{enrichment.meetingWith || '-'}</td>
                        <td>{share.DueDate ? new Date(share.DueDate).toLocaleDateString() : '-'}</td>
                        <td>
                          {enrichment.appointmentMatchType === 'closest-guess' ? (
                            <span
                              title="Best guess: nearest appointment for this customer by due date. Not confirmed via an order link."
                              className="lookout-guess-date"
                            >
                              <i className="fas fa-question-circle"></i>
                              {formatAppointmentDateTime(enrichment.appointmentDate)}
                            </span>
                          ) : (
                            formatAppointmentDateTime(enrichment.appointmentDate)
                          )}
                        </td>
                        <td>{enrichment.currentRoom || '-'}</td>
                        <td>{filterNextRooms(enrichment.nextRooms) || '-'}</td>
                        <td>{share.SharedBy}</td>
                        <td className="lookout-inbox-actions">
                          <button
                            onClick={() => handleAddShare(share)}
                            disabled={isPending}
                            className="btn-small btn-select"
                          >
                            Add to My List
                          </button>
                          <button
                            onClick={() => handleDismissShare(share)}
                            disabled={isPending}
                            className="btn-small"
                            title="Dismiss without adding"
                          >
                            Dismiss
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {sharePickerItems && (
        <SharePickerModal
          items={sharePickerItems}
          onClose={() => setSharePickerItems(null)}
          onShared={refreshShares}
        />
      )}

      <div className="PageShadowTable" style={{ padding: '15px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
          <h2>Past Due Tasks - Machine (1002), Comb, Clips, Band... (1003), Color (2002), Hand Sew (3001), Treatment (4001), Wash &amp; Set (5001) &amp; Rachel's Room (6001)</h2>
          {pastDueOpened && (
            <button
              onClick={fetchPastDueTasks}
              disabled={loadingPastDueTasks}
              className="btn-small btn-select"
              style={{ padding: '8px 16px', fontSize: '14px' }}
            >
              {loadingPastDueTasks ? 'Refreshing...' : 'Refresh'}
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap' }}>
          <div className="past-due-tabs">
            {[
              { value: 'all', label: 'All Rooms' },
              { value: '1002', label: 'Machine (1002)' },
              { value: '1003', label: 'Comb, Clips, Band... (1003)' },
              { value: '2002', label: 'Color (2002)' },
              { value: '3001', label: 'Hand Sew (3001)' },
              { value: '4001', label: 'Treatment (4001)' },
              { value: '5001', label: 'Wash & Set (5001)' },
              { value: '6001', label: "Rachel's Room (6001)" }
            ].map(tab => (
              <button
                key={tab.value}
                onClick={() => handlePastDueRoomTab(tab.value)}
                className={`past-due-tab ${pastDueOpened && pastDueRoomFilter === tab.value ? 'active' : ''}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="past-due-tabs">
            {[
              { value: 'all', label: 'All Orders' },
              { value: 'stock', label: 'Stock' },
              { value: 'client', label: 'Client' }
            ].map(tab => (
              <button
                key={tab.value}
                onClick={() => handlePastDueOrderTypeTab(tab.value)}
                className={`past-due-tab ${pastDueOpened && pastDueOrderTypeFilter === tab.value ? 'active' : ''}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {pastDueOpened && (
            <span style={{ fontSize: '13px', color: '#666' }}>
              Showing {filteredPastDueTasks.length} of {pastDueTasks.length}
            </span>
          )}
        </div>

        {!pastDueOpened ? (
          <p>Select a room or order type above to load past due tasks.</p>
        ) : loadingPastDueTasks ? (
          <p>Loading past due tasks...</p>
        ) : pastDueTasks.length === 0 ? (
          <p>No past due tasks in these rooms.</p>
        ) : filteredPastDueTasks.length === 0 ? (
          <p>No past due tasks match the selected filters.</p>
        ) : (
          <div className="room-tasks-table-container-past-due">
            <table className="room-tasks-table-container-past-due">
              <thead>
                <tr>
                  <th>Order No</th>
                  <th>Type</th>
                  <th>Barcode</th>
                  <th>Customer</th>
                  <th>Task Notes</th>
                  <th>QC Notes</th>
                  <th>Assigned Worker</th>
                  <th>Due Date</th>
                  <th
                    onClick={handleToggleDaysOverdueSort}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort"
                  >
                    Days Overdue {daysOverdueSortOrder === 'asc' ? '▲' : '▼'}
                  </th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredPastDueTasks.map((task) => {
                  const isExpanded = expandedPastDueRows.has(task.OrderNo);
                  return (
                  <React.Fragment key={task.ID}>
                    <tr
                      className={`past-due-row ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => handleTogglePastDueRow(task.OrderNo)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <span className={`expand-icon ${isExpanded ? 'rotated' : ''}`}>▶</span>
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewPastDueTask(task);
                          }}
                          className="past-due-order-link"
                        >
                          {formatStockWigOrderNo(task.OrderNo)}
                        </span>
                      </td>
                      <td>{renderOrderTypeBadge(task.OrderNo)}</td>
                      <td>{task.WigBarCode}</td>
                      <td>{task.OrderCustomerName || '-'}</td>
                      <td>{task.TaskNotes || '-'}</td>
                      <td>{task.QC_Notes || '-'}</td>
                      <td>{task.AssignedWorker || '-'}</td>
                      <td>{task.TaskDueDate ? new Date(task.TaskDueDate).toLocaleDateString() : '-'}</td>
                      <td style={{ fontWeight: 'bold', color: '#c0392b' }}>{task.DaysOverdue}</td>
                      <td>{task.TaskStatus || '-'}</td>
                    </tr>
                    {isExpanded && (
                      <tr className="expanded-content">
                        <td colSpan={10}>
                          <ProductionSchedule orderNo={task.OrderNo} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PastDueTasks;
